import { type FieldDatabase, FLOORPLAN_BLOB_SOURCES, type FloorplanBlobRecord } from '@/shared/db/schema';

export class FloorplanBlobRepository {
  constructor(private readonly database: FieldDatabase) {}

  async get(input: { buildingFloor: string; buildingId: number | string | null; locationId: number | string }) {
    return hydrateBlobRecord(await this.database.get('floorplan_blobs', createFloorplanBlobId(input)));
  }

  async getById(id: string) {
    return hydrateBlobRecord(await this.database.get('floorplan_blobs', id));
  }

  async save(record: Omit<FloorplanBlobRecord, 'id'>) {
    const savedRecord = {
      ...record,
      id: createFloorplanBlobId(record),
    };

    await this.database.put(
      'floorplan_blobs',
      (await serializeBlobRecord(savedRecord)) as unknown as FloorplanBlobRecord,
    );

    return savedRecord;
  }

  async markUploaded(id: string, input: { floorplanId?: string | number; uploadedAt: string }) {
    const record = await this.getById(id);

    if (!record) {
      return undefined;
    }

    const updated: FloorplanBlobRecord = {
      ...record,
      floorplanId: input.floorplanId ? String(input.floorplanId) : record.floorplanId,
      pendingUpload: false,
      source: FLOORPLAN_BLOB_SOURCES.remote,
      uploadedAt: input.uploadedAt,
    };

    await this.database.put('floorplan_blobs', (await serializeBlobRecord(updated)) as unknown as FloorplanBlobRecord);

    return updated;
  }
}

async function serializeBlobRecord(record: FloorplanBlobRecord) {
  return {
    ...record,
    blob: await new Response(record.blob).arrayBuffer(),
  };
}

function hydrateBlobRecord(record: FloorplanBlobRecord | undefined): FloorplanBlobRecord | undefined {
  if (!record) {
    return undefined;
  }

  if (record.blob instanceof Blob) {
    return record;
  }

  const storedBlob = record.blob as unknown;

  if (storedBlob instanceof ArrayBuffer) {
    return {
      ...record,
      blob: new Blob([storedBlob], { type: record.mimeType }),
    };
  }

  if (ArrayBuffer.isView(storedBlob)) {
    const bytes = new Uint8Array(storedBlob.byteLength);
    bytes.set(new Uint8Array(storedBlob.buffer, storedBlob.byteOffset, storedBlob.byteLength));

    return {
      ...record,
      blob: new Blob([bytes.buffer as ArrayBuffer], { type: record.mimeType }),
    };
  }

  return record;
}

export function createFloorplanBlobId(input: {
  buildingFloor: string;
  buildingId: number | string | null;
  locationId: number | string;
}) {
  return `floorplan:${input.locationId}:${input.buildingId ?? 'location'}:${input.buildingFloor}`;
}
